import { useApiUrl, useInvalidate, useShow } from '@refinedev/core'
import { useParams } from 'react-router'
import { useCallback } from 'react'
import type { TPublisherRequest } from '@modules/types'
import { PUBLISHER_EXPAND_PRESETS } from '@modules/api'
import {
  isCopyrightRequest,
  PublisherApproveRequestStatus,
  PublisherRequestTypeEnum,
} from '@modules/api-core'

const NOT_FOUND = [403, 404]

export const useRequestShowData = () => {
  const apiUrl = useApiUrl()
  const invalidate = useInvalidate()
  const { id } = useParams<{ id: string }>()

  const requestId = id ? parseInt(id, 10) : undefined

  const { query } = useShow<TPublisherRequest>({
    resource: 'publisher-request',
    id: requestId,
    meta: PUBLISHER_EXPAND_PRESETS.FULL,
    queryOptions: {
      enabled: !!requestId,
      retry: (failureCount, error) => !NOT_FOUND.includes(error.statusCode) && failureCount < 3,
    },
  })

  const { data, isFetching: isFetchingRequest, isError } = query
  const selectedRequest = data?.data

  const isRequestSubmitted = useCallback(() => {
    if (!selectedRequest?.songs || selectedRequest.songs.length === 0) return false

    const requestType = selectedRequest.type

    if (isCopyrightRequest(requestType)) {
      return selectedRequest.songs.every(
        song =>
          ((selectedRequest.asksPrintShare !== 1 && selectedRequest.asksDigitalShare !== 1) ||
            song.asksShares === 0 ||
            (song.claimedShares && song.claimedShares.length > 0)) &&
          (selectedRequest.asksNotice !== 1 ||
            Boolean(song.submittedCopyrightNotice) ||
            Boolean(song.claimedShares?.some(share => share.isNotOwned === 1))),
      )
    }

    if (requestType === PublisherRequestTypeEnum.COMMON_APPROVAL_REQUEST) {
      return (
        selectedRequest.approveStatus === PublisherApproveRequestStatus.APPROVED ||
        selectedRequest.approveStatus === PublisherApproveRequestStatus.REJECTED
      )
    }

    if (
      requestType === PublisherRequestTypeEnum.APPROVAL_REQUEST ||
      requestType === PublisherRequestTypeEnum.LYRICS_APPROVAL_REQUEST
    ) {
      return selectedRequest.songs.every(
        song => song.approvalDecision !== null && song.approvalDecision !== undefined,
      )
    }

    return false
  }, [selectedRequest])

  return {
    id: requestId,
    selectedRequest,
    isFetchingRequest,
    isError,
    isNotFound: NOT_FOUND.includes(query.error?.statusCode ?? 0),
    isRequestSubmitted: isRequestSubmitted(),
    query,
    apiUrl,
    invalidate,
  }
}
